import { useState, type FC } from 'react';
import { Dropdown, FloatButton, Spin, Tooltip } from 'antd';
import { CustomerServiceOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';

import { getVoipStatuses, postVoipLogin, postVoipLogout, postVoipPause, postVoipResume } from 'queries';
import { type JsonData, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { Toast, parseErrors } from 'helpers';
import styles from './switchvox.module.scss';

export type Props = WithOptionalDataTest;

enum VoipStatuses {
  Login = 'LOGIN',
  Available = 'AVAILABLE',
  Pause = 'PAUSE',
  Logout = 'LOGOUT',
}

const useVoipStatuses = (callBack: any) => {
  return useQuery(['voipStatuses'], getVoipStatuses, {
    onSuccess: (data) => {
      callBack(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
};

export const useVoipMutation = (mutation: any, callBack?: (statuses: JsonData) => void) => {
  return useMutation(mutation, {
    onSuccess: (data: JsonDataItem) => {
      callBack?.(data.statuses);
      Toast.show({ description: data.message, type: Toast.Type.Success });
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });
};

const formatStatusList = (statuses: JsonData | undefined): JsonData => {
  if (!statuses) {
    return [];
  }

  const groupedItems: JsonData = []; // group items by action type
  const addedActions = new Set(); // keep track of already added actions

  statuses.forEach((item) => {
    const action = item.action;
    if (!addedActions.has(action)) {
      // if action not added yet, add divider and group items
      if (addedActions.size > 0) {
        groupedItems.push({ type: 'divider' });
      }
      addedActions.add(action);
      statuses
        .filter((i) => i.action === action)
        .forEach((groupedItem) => {
          groupedItems.push(groupedItem as any);
        });
    }
  });

  // add group dividers and "key" prop to make Dropdown working
  return groupedItems.map((item: any) => {
    if (item.type === 'divider') {
      return item;
    } else {
      const { ID, ...rest } = item; // prevent console warning
      return { ...rest, key: ID.toString() };
    }
  });
};

export const Switchvox: FC<Props> = observer(() => {
  const [voipStatuses, setVoipStatuses] = useState<JsonData>();
  const currentStatus = voipStatuses?.find((status: JsonDataItem) => status.currentStatus);
  const hasBadge = currentStatus?.status === VoipStatuses.Available || currentStatus?.status === VoipStatuses.Pause;
  const isInactive = currentStatus?.status === VoipStatuses.Login || currentStatus?.status === undefined;

  const { isLoading: loadingVoipStatuses, error: statusError } = useVoipStatuses(setVoipStatuses);
  const { mutate: loginVoip, isLoading: loginingVoip } = useVoipMutation(postVoipLogin, setVoipStatuses);
  const { mutate: logoutVoip, isLoading: logoutingVoip } = useVoipMutation(postVoipLogout, setVoipStatuses);
  const { mutate: pauseVoip, isLoading: pausingVoip } = useVoipMutation(postVoipPause, setVoipStatuses);
  const { mutate: resumeVoip, isLoading: resumingVoip } = useVoipMutation(postVoipResume, setVoipStatuses);

  const handleStatusClick = ({ key }: JsonDataItem) => {
    const newStatus = voipStatuses?.find((status) => status.ID?.toString() === key);
    if (key === currentStatus?.ID.toString() && currentStatus?.status !== VoipStatuses.Login) {
      return;
    }

    switch (newStatus?.status) {
      case VoipStatuses.Login: {
        loginVoip();
        return;
      }
      case VoipStatuses.Logout: {
        logoutVoip();
        return;
      }
      case VoipStatuses.Available: {
        resumeVoip();
        return;
      }
      case VoipStatuses.Pause: {
        pauseVoip(newStatus?.reason);
      }
    }
  };

  const getStatusColor = {
    [VoipStatuses.Available]: 'green',
    [VoipStatuses.Pause]: 'orange',
    [VoipStatuses.Logout]: 'red',
    [VoipStatuses.Login]: 'red',
  };

  return (
    <FloatButton
      type={isInactive ? 'default' : 'primary'}
      icon={
        <Dropdown
          placement="topLeft"
          menu={{
            items: formatStatusList(voipStatuses) as any,
            defaultSelectedKeys: isInactive ? undefined : [currentStatus?.ID.toString()],
            selectedKeys: isInactive ? undefined : [currentStatus?.ID.toString()],
            selectable: true,
            onClick: handleStatusClick,
          }}
        >
          <div className={cx(styles.switchvox, !isInactive && styles.switchvox_alt)}>
            {loginingVoip || logoutingVoip || pausingVoip || resumingVoip || loadingVoipStatuses ? (
              <Spin />
            ) : (
              <Tooltip title={statusError && parseErrors(statusError).join(', ')}>
                <CustomerServiceOutlined />
              </Tooltip>
            )}
          </div>
        </Dropdown>
      }
      badge={{
        color: getStatusColor[currentStatus?.status as VoipStatuses] as any,
        dot: hasBadge,
      }}
      shape="square"
      style={{ left: 20, bottom: 68, zIndex: 'var(--zindex-fixed)' }}
    />
  );
});
